// React Router v6 migration complete
// Unified routing with AppRoutes - replaces individual admin pages
import React, {useEffect} from 'react'
import {Navigate, Route, Routes, useNavigate} from 'react-router-dom'
import {
  updateCurrentUser,
  setAppLoaded,
  updateLoggedInCompanyId,
} from '~/src/store/slices/root-slice'
import PrivateRoute from './components/common/PrivateRoute'
import {Loader} from './components/ui/loader'
import RegistrationPage from './pages/Registration'
import RoleSelectPage from './pages/RoleSelect'
import PageNotFound, {NotAuthorized} from './pages/sign-in/404'
import CreateAccountPortal from './pages/sign-in/CreateAccountPortal'
import CreateAgency from './pages/sign-in/CreateAgency'
import CreateCompany from './pages/sign-in/CreateCompany'
import ForgotPassword from './pages/sign-in/ForgotPassword'
import PasswordConfirmation from './pages/sign-in/PasswordConfirmation'
import PasswordResetPage from './pages/sign-in/PasswordReset'
import RegistrationSuccess from './pages/sign-in/RegistrationSuccess'
import SigninPage from './pages/sign-in/Signin'
import OAuthCallback from './pages/oauth/OAuthCallback'
import OAuthWelcome from './pages/oauth/OAuthWelcome'
import UnauthorizedPage from './pages/UnauthorizedPage'
import {AppRoutes} from './routes/AppRoutes'
import {BASE_ROUTES} from './routes/constants'
import {useLazyGetCurrentUserQuery} from './store/apis/user-api'
import {redirect} from './store/slices/root-slice'
import {useAppDispatch, useAppSelector} from './store/store-hooks'
import {isNullOrUndefined} from './utils/type-validators'
import {isTokenExpired} from './utils/jwt-utils'
import {configurePresenceSocketAuth} from './services/presenceSocket'
import {store} from './store'
import {reportCatchError} from './utils/errorReporter'

export default function App(): JSX.Element {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const {redirectTo, appIsLoaded} = useAppSelector((state) => state.root)
  const [getCurrentUser] = useLazyGetCurrentUserQuery()

  useEffect(() => {
    configurePresenceSocketAuth(() => store.getState().root.token ?? undefined)
  }, [])

  // Boot-time session restore. redux-persist rehydrates token, role, and
  // permissions, but NOT currentUser or loggedInCompanyId. Without this, a hard
  // refresh leaves currentUser null and PrivateRoute bounces to /signin — i.e.
  // refreshing logs the user out. (The old partialUser-based restore was orphaned
  // during the Cognito→IOP migration: nothing ever sets partialUser.)
  //
  // When a valid token is persisted, re-fetch the user via /user/me — the backend
  // resolves identity from the JWT, so this works for both Cognito and IOP tokens
  // — and re-establish the session in place. We hold appIsLoaded=false until the
  // fetch settles so routes (and PrivateRoute) only render once currentUser is set,
  // which avoids a redirect flash. No navigation: the user stays on the route they
  // refreshed.
  useEffect(() => {
    const restoreSession = async () => {
      const {token, currentUser} = store.getState().root
      const hasValidToken = !isNullOrUndefined(token) && token !== 'null' && !isTokenExpired(token)

      if (hasValidToken && isNullOrUndefined(currentUser)) {
        try {
          const user = await getCurrentUser().unwrap()
          dispatch(updateCurrentUser(user))
          dispatch(
            updateLoggedInCompanyId(
              user.companyId && user.companyId.length > 0 ? user.companyId : '',
            ),
          )
        } catch (error) {
          reportCatchError('Failed to restore session from token:', error)
        }
      }

      dispatch(setAppLoaded(true))
    }

    restoreSession()
    // Run once on mount; reads token/currentUser imperatively from the store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isNullOrUndefined(redirectTo)) {
      navigate(redirectTo)
      dispatch(redirect())
    }
  }, [redirectTo, dispatch, navigate])

  if (!appIsLoaded) return <Loader page="mini" size="large" />

  return (
    <Routes>
      {/* Public routes */}
      <Route path={`${BASE_ROUTES.REGISTRATION}/*`} element={<RegistrationPage />} />
      <Route path={`${BASE_ROUTES.CREATE_ACCOUNT_PORTAL}/*`} element={<CreateAccountPortal />} />
      <Route path={BASE_ROUTES.REGISTRATION_SUCCESS} element={<RegistrationSuccess />} />
      <Route path={`${BASE_ROUTES.CREATE_COMPANY}/*`} element={<CreateCompany />} />
      <Route path={`${BASE_ROUTES.CREATE_AGENCY}/*`} element={<CreateAgency />} />
      <Route
        path={`${BASE_ROUTES.FORGOT_PASSWORD}/:email?/:confirmationCode?`}
        element={<ForgotPassword />}
      />
      <Route path={BASE_ROUTES.PASSWORD_RESET} element={<PasswordResetPage />} />
      <Route path={BASE_ROUTES.PASSWORD_CONFIRMATION} element={<PasswordConfirmation />} />
      <Route path={`${BASE_ROUTES.SIGNIN}/:emailAddress?/:token?`} element={<SigninPage />} />
      <Route path={BASE_ROUTES.OAUTH_CALLBACK} element={<OAuthCallback />} />
      <Route path={BASE_ROUTES.OAUTH_WELCOME} element={<OAuthWelcome />} />

      {/* Role selection page */}
      <Route
        path={`${BASE_ROUTES.ROLE_SELECT}/*`}
        element={
          <PrivateRoute>
            <RoleSelectPage />
          </PrivateRoute>
        }
      />

      {/* Protected routes - Single unified route (variant determined from user role) */}
      <Route
        path="/*"
        element={
          <PrivateRoute>
            <AppRoutes />
          </PrivateRoute>
        }
      />

      {/* Redirect root to signin */}
      <Route path={BASE_ROUTES.ROOT} element={<Navigate to={BASE_ROUTES.SIGNIN} replace />} />

      {/* Error routes */}
      <Route path={BASE_ROUTES.NOT_AUTHORIZED} element={<NotAuthorized />} />
      <Route
        path={BASE_ROUTES.UNAUTHORIZED}
        element={
          <PrivateRoute>
            <UnauthorizedPage />
          </PrivateRoute>
        }
      />
      <Route path={BASE_ROUTES.NO_MATCH} element={<PageNotFound />} />
    </Routes>
  )
}
